import * as THREE from 'three'

export type CameraMode = 'inseguimento' | 'libera'

export interface CameraRig {
  mode: CameraMode
  toggle(): void
  attach(dom: HTMLElement): void
  update(camera: THREE.PerspectiveCamera, target: THREE.Vector3, up: THREE.Vector3, raggioVeicolo: number): void
}

/**
 * Due camere.
 *
 * "Inseguimento" orbita attorno al veicolo tenendo come alto la VERTICALE
 * LOCALE, non +Y del mondo: altrimenti, spostandosi sul pianeta, l'orizzonte
 * si capovolgerebbe e non si capirebbe piu nulla.
 *
 * "Libera" resta ferma nel mondo e guarda il veicolo: serve a vedere la
 * separazione degli stadi, che dalla camera inseguitrice non si apprezza.
 */
export function createCameraRig(): CameraRig {
  let mode: CameraMode = 'inseguimento'
  let yaw = 0.6
  let pitch = 0.22
  let distanza = 0
  let distanzaVoluta = 0
  let inquadrataUnaVolta = false
  const ancora = new THREE.Vector3()
  let ancorata = false

  let trascinando = false
  let ultimoX = 0
  let ultimoY = 0

  const rig: CameraRig = {
    get mode() { return mode },
    set mode(m: CameraMode) { mode = m; ancorata = false },

    toggle() { mode = mode === 'inseguimento' ? 'libera' : 'inseguimento'; ancorata = false },

    attach(dom) {
      dom.addEventListener('pointerdown', (e) => {
        trascinando = true; ultimoX = e.clientX; ultimoY = e.clientY
      })
      window.addEventListener('pointerup', () => { trascinando = false })
      window.addEventListener('pointermove', (e) => {
        if (!trascinando) return
        yaw -= (e.clientX - ultimoX) * 0.006
        pitch = Math.max(-1.45, Math.min(1.45, pitch + (e.clientY - ultimoY) * 0.006))
        ultimoX = e.clientX; ultimoY = e.clientY
      })
      dom.addEventListener('wheel', (e) => {
        e.preventDefault()
        distanzaVoluta = Math.max(8, Math.min(4000, distanzaVoluta * Math.exp(e.deltaY * 0.0012)))
      }, { passive: false })
    },

    update(camera, target, up, raggioVeicolo) {
      if (!inquadrataUnaVolta) {
        // al primo frame si inquadra il veicolo: la distanza esce dalla sua
        // taglia, non da un numero scelto a caso
        distanzaVoluta = Math.max(22, raggioVeicolo * 3.4)
        distanza = distanzaVoluta
        inquadrataUnaVolta = true
      }
      const minima = Math.max(12, raggioVeicolo * 2.4)
      distanzaVoluta = Math.max(minima * 0.35, distanzaVoluta)
      distanza += (distanzaVoluta - distanza) * 0.12

      if (mode === 'libera') {
        if (!ancorata) {
          // si pianta dove si trovava e guarda il veicolo allontanarsi
          ancora.copy(target).addScaledVector(up, raggioVeicolo * 1.2)
          ancora.x += distanza * 0.8
          ancorata = true
        }
        camera.position.copy(ancora)
        camera.up.copy(up)
        camera.lookAt(target)
        return
      }

      // terna orizzontale attorno alla verticale locale
      const u = up.clone().normalize()
      const riferimento = Math.abs(u.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0)
      const e1 = new THREE.Vector3().crossVectors(riferimento, u).normalize()
      const e2 = new THREE.Vector3().crossVectors(u, e1).normalize()

      const orizzontale = Math.cos(pitch) * distanza
      const offset = new THREE.Vector3()
        .addScaledVector(e1, Math.cos(yaw) * orizzontale)
        .addScaledVector(e2, Math.sin(yaw) * orizzontale)
        .addScaledVector(u, Math.sin(pitch) * distanza + raggioVeicolo * 0.4)

      camera.position.copy(target).add(offset)
      camera.up.copy(u)
      camera.lookAt(target)
    },
  }

  return rig
}
